<template>
  <div :class="['mastery-mobile-menu-component', { active }]">
    <div class="menu-wrapper">
      <div class="menu-header">
        <button
          class="close-btn"
          aria-label="Close"
          @click="closeMenu"
        >
          <icon
            name="cross-ultra-thin"
            variant="181d27"
            class="close-icon"
          />
        </button>
      </div>
      <search-bar-simple
        @close="closeMenu"
      ></search-bar-simple>
      <ul class="menu-links">
        <li
          v-for="navItem in navigationItems"
          :key="navItem.id"
          :class="{ active: navItemActive(navItem) }"
          class="menu-link-item text-md font-semibold text-secondary-700"
        >
          <router-link
            :to="navItem.route"
            class="menu-link"
            @click="navItemClicked(navItem, closeMenu)"
          >
            <img
              :src="navItemIcon(navItem)"
              class="link-icon"
              alt=""
              role="presentation"
            >
            <span class="nav-item-name">
              {{ navItem.name }}
            </span>
            <span class="flex-1"></span>
            <span>
              <icon
                name="arrow"
                variant="a4a7ae"
                class="chevron-icon"
              />
            </span>
          </router-link>
        </li>
        <li class="menu-link-item question-sets-item text-md font-semibold text-secondary-700">
          <question-sets-sidebar-link mobile />
        </li>
        <li class="menu-link-item study-planner-item text-md font-semibold text-secondary-700">
          <study-planner-sidebar-link mobile />
        </li>
        <li class="menu-link-item tag-search-item text-md font-semibold text-secondary-700">
          <tag-search-sidebar-link mobile />
        </li>
        <li class="menu-link-item discussion-board-item text-md font-semibold text-secondary-700">
          <discussion-board-sidebar-link mobile />
        </li>
      </ul>
      <div class="flex-1"></div>
      <ul class="menu-links">
        <li
          v-for="link in menuLinks"
          :key="link.name"
          class="menu-link-item text-md font-semibold text-secondary-700"
          data-test="account-link"
        >
          <router-link
            :to="link.route"
            class="menu-link"
            @click="closeMenu"
          >
            <icon
              :name="link.icon"
              class="link-icon"
            />
            {{ link.name }}
          </router-link>
        </li>
      </ul>
      <div class="footer">
        <button
          v-if="showUpgrade"
          class="join-btn"
          @click="showUpsell"
        >
          <icon
            class="lock-icon"
            name="lock-slot"
          />
          <span class="text-md font-semibold">Join</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@components/singletons/icon';
import QuestionSetsSidebarLink from '@components/question-sets/SidebarLink';
import StudyPlannerSidebarLink from '@components/study-planner/SidebarLink';
import DiscussionBoardSidebarLink from '@components/discussion-board/SidebarLink';
import TagSearchSidebarLink from '@components/tag-search/SidebarLink';
import SearchBarSimple from '@components/singletons/SearchbarSimple';
import DashboardMixin from '@/mixins/dashboard';
import DeviceTypeMixin from '@/mixins/deviceType';
import NavItemsMixin from '@/mixins/navItems';

const links = {
  authenticated: [
    {
      name: 'Profile',
      route: { name: 'mastery#profile' },
      icon: 'gear',
    },
    {
      name: 'Support',
      route: { name: 'support' },
      icon: 'speech-bubbles',
    },
    {
      name: 'Sign Out',
      route: { name: 'signOut' },
      icon: 'sign-out',
    },
  ],
  default: [
    {
      name: 'Support',
      route: { name: 'support' },
      icon: 'phone-outline',
    },
    {
      name: 'Sign In',
      route: { name: 'signIn' },
      icon: 'profile',
    },
  ],
  downloadApp: {
    name: 'Download App',
    route: { name: 'downloadApp' },
    icon: 'download',
  },
};

export default {
  components: { DiscussionBoardSidebarLink, Icon, QuestionSetsSidebarLink, SearchBarSimple, StudyPlannerSidebarLink, TagSearchSidebarLink },
  mixins: [DashboardMixin, DeviceTypeMixin, NavItemsMixin],
  computed: {
    active() {
      return this.$store.getters['session/showMobileMenu'];
    },
    activeItem() {
      // if route has no ties to web-main group, don't do anything
      // secondaryRoute on meta just means it doesn't live in main content
      if (this.$route.meta.secondaryRoute) return {};
      // see if one of the items matches query directly first
      const queryItem = this.navigationItems.find(ni => ni.matchesRouteQuery(this.$route));
      if (queryItem) return queryItem;

      // otherwise see if last visited dashboard has a matching menu item
      const lastDashboardIds = this.$store.getters['dashboards/lastVisitedId'];
      const dashboardItemMatch = this.navigationItems.find(ni => {
        // Dashboard group ids can be in the lastVisitedId array so we want to check
        // if the first item in the lastVisitedId array is the id of a dashboard group
        // that is found in the main nav. This will make sure that we are only matching
        // the ids of the dashboard group with the root level id in lastDashboardIds to
        // prevent checking dashboard ids against dashboard group ids.
        if (ni.destination === 'dashboard_group') {
          return lastDashboardIds[0] === ni.resourceId;
        }

        return ni.destination === 'dashboard' && lastDashboardIds.includes(ni.resourceId);
      });

      if (dashboardItemMatch) return dashboardItemMatch;

      // otherwise see if there's a home menu item that matches the last dashboard item
      const homeDashboard = this.$store.getters['dashboards/menuDefaultDashboard'];
      if (homeDashboard) {
        const homeItemMatch = this.navigationItems.find(ni => {
          return ni.destination === 'dashboard' && ni.resourceId === homeDashboard.id;
        });

        if (homeItemMatch) return homeItemMatch;
      }

      // lastly see if there's a home item and our path is /
      if (this.$route.path === '/') {
        const homeItemPathMatch = this.navigationItems.find(ni => {
          return ni.destination === 'home';
        });

        if (homeItemPathMatch) return homeItemPathMatch;
      }

      return {};
    },
    authenticated() {
      return this.$store.getters['session/authenticated'];
    },
    menuLinks() {
      let menuLinks = links.default;
      if (this.authenticated) {
        menuLinks = links.authenticated;
      }
      if (this.isIOS || this.isAndroid) {
        menuLinks.push(links.downloadApp);
      }
      return menuLinks;
    },
    nonSubscriber() {
      return !this.$store.getters['session/currentUser']?.subscriptionReceipt;
    },
    showUpgrade() {
      const pvar = this.$store.getters['productVars/get']['disable_web_top_bar_upgrade'];
      const disabledByPVar = pvar && (pvar.parsedValue === true);
      return this.nonSubscriber && !disabledByPVar;
    },
  },
  methods: {
    closeMenu() {
      this.$store.commit('session/toggleMobileMenu', false);
    },
    showUpsell() {
      this.$store.dispatch('ui/toggleUpsellModal', { show: true });
    },
  },
};
</script>

<style lang="scss" scoped>
.mastery-mobile-menu-component {
  $menu-width: calc(100vw - 65px);

  position: fixed;
  left: 100vw;
  right: 0;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 1000;
  display: none;
  width: $menu-width;
  background-color: #ffffff;
  flex-direction: column;
  transition: .25s ease-in left;

  @include breakpoint(small) {
    display: flex;
  }

  &.active {
    left: calc(100vw - #{$menu-width});

    &::before {
      content: "";
      position: absolute;
      top: 0;
      right: $menu-width;
      background: rgba(0, 0, 0, .39);
      width: 100vw;
      height: 100vh;
    }
  };

  .menu-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
    padding: rem-calc(22);
  }

  .menu-header {
    display: flex;
    justify-content: flex-end;
    margin-bottom: rem-calc(22);

    .close-btn {
      border: none;
      background: transparent;
      padding: 0;

      .close-icon {
        width: rem-calc(14);
        height: rem-calc(14);
      }
    }
  }

  .menu-links {
    margin: 0;
    padding-inline-start: 0;
    padding: 0 rem-calc(8);
    list-style-type: none;

    .menu-link-item {
      margin-top: rem-calc(26);
      margin-bottom: rem-calc(26);

      &.active {
        border-radius: var(--radius-sm);
        box-shadow: inset 0 0 0 500px var(--bg-light),
                    0 0 0 12px var(--bg-light);
      }

      .menu-link {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: rem-calc(8);
        text-decoration: none;
        color: inherit;
        vertical-align: middle;
      }

      .link-icon {
        vertical-align: middle;
        width: rem-calc(20);
      }

      // The link renders nothing for users who can't see question sets
      &.question-sets-item:empty,
      &.study-planner-item:empty,
      &.tag-search-item:empty,
      &.discussion-board-item:empty {
        display: none;
      }
    }
  }

  .footer {
    .join-btn {
      width: 100%;
      padding: rem-calc(10) 0;
      border-radius: var(--radius-md);
      background: var(--Component-colors-Utility-Gray-utility-gray-100, rgba(245, 245, 245, 1));
      border: 1px solid var(--Colors-Border-border-primary, rgba(213, 215, 218, 1));
      display: flex;
      align-items: center;
      justify-content: center;
      gap: rem-calc(8);
    }
  }
}
</style>
